/**
 * Lecture d'une réponse Overpass et filtres d'exclusion —
 * EF-POI-008, EF-POI-013, EF-POI-009.
 */

import { categoriesForTags, categoryDefinition, matchedTags, type CategoryKey } from '../core/categories';
import type { Coordinate } from '../geo/geodesy';
import type { OsmType } from '../core/model';

export interface OsmObject {
  readonly type: OsmType;
  readonly id: number;
  readonly tags: Readonly<Record<string, string>>;
  readonly coordinate: Coordinate;
}

export interface OverpassResponse {
  readonly objects: readonly OsmObject[];
  /**
   * Présent lorsque Overpass signale un dépassement survenu de son côté. Un
   * tronçon dont la réponse porte un `remark` doit être traité comme n'ayant
   * pas abouti (EF-POI-009).
   */
  readonly remark: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readTags(value: unknown): Record<string, string> {
  const record = asRecord(value);
  if (record === null) return {};
  const tags: Record<string, string> = {};
  for (const [key, raw] of Object.entries(record)) {
    if (typeof raw === 'string') tags[key] = raw;
  }
  return tags;
}

function readCoordinate(element: Record<string, unknown>): Coordinate | null {
  const lat = element['lat'];
  const lon = element['lon'];
  if (typeof lat === 'number' && typeof lon === 'number') return { lat, lon };

  // `out center` fournit une coordonnée représentative pour les chemins et
  // les relations, qui n'en possèdent pas.
  const center = asRecord(element['center']);
  if (center !== null) {
    const centerLat = center['lat'];
    const centerLon = center['lon'];
    if (typeof centerLat === 'number' && typeof centerLon === 'number') {
      return { lat: centerLat, lon: centerLon };
    }
  }
  return null;
}

const OSM_TYPES: readonly string[] = ['node', 'way', 'relation'];

/** Analyse le corps JSON d'une réponse de code 200. */
export function parseOverpassResponse(payload: unknown): OverpassResponse {
  const root = asRecord(payload);
  if (root === null) return { objects: [], remark: null };

  const remarkValue = root['remark'];
  const remark = typeof remarkValue === 'string' && remarkValue.length > 0 ? remarkValue : null;

  const elements = root['elements'];
  if (!Array.isArray(elements)) return { objects: [], remark };

  const objects: OsmObject[] = [];
  for (const raw of elements) {
    const element = asRecord(raw);
    if (element === null) continue;
    const type = element['type'];
    const id = element['id'];
    if (typeof type !== 'string' || !OSM_TYPES.includes(type)) continue;
    if (typeof id !== 'number') continue;
    const coordinate = readCoordinate(element);
    if (coordinate === null) continue;
    objects.push({ type: type as OsmType, id, tags: readTags(element['tags']), coordinate });
  }

  return { objects, remark };
}

export interface ExclusionVerdict {
  readonly rejected: boolean;
  /** Cause nommée, par exemple `access=private`. */
  readonly cause: string | null;
}

const ACCEPTED = { rejected: false, cause: null } as const;

/**
 * Filtres d'exclusion de EF-POI-013, appliqués **localement** sur les objets
 * reçus. Un objet portant `access=customers` est conservé et porte la mention
 * « réservé aux clients ».
 */
export function evaluateExclusion(
  tags: Readonly<Record<string, string>>,
  categories: readonly CategoryKey[],
): ExclusionVerdict {
  const access = tags['access'];
  if (access === 'private' || access === 'no') {
    return { rejected: true, cause: `access=${access}` };
  }

  if (categories.includes('water') && tags['drinking_water'] === 'no') {
    return { rejected: true, cause: 'drinking_water=no' };
  }

  const openingHours = tags['opening_hours'];
  if (openingHours === 'closed' || openingHours === 'off') {
    return { rejected: true, cause: `opening_hours=${openingHours}` };
  }

  const disused = disusedCause(tags, categories);
  if (disused !== null) return { rejected: true, cause: disused };

  return ACCEPTED;
}

/**
 * Vrai lorsque `disused`, ou un préfixe `disused:`, porte le tag qui aurait
 * déterminé la catégorie.
 */
function disusedCause(
  tags: Readonly<Record<string, string>>,
  categories: readonly CategoryKey[],
): string | null {
  if (tags['disused'] === 'yes') return 'disused=yes';

  const determiningKeys = new Set<string>();
  for (const key of categories) {
    for (const match of categoryDefinition(key).matches) {
      for (const condition of match.conditions) determiningKeys.add(condition.key);
    }
  }

  for (const tagKey of Object.keys(tags)) {
    if (!tagKey.startsWith('disused:')) continue;
    if (determiningKeys.has(tagKey.slice('disused:'.length))) return `${tagKey}`;
  }
  return null;
}

/** Mention d'accès restreint conservée dans `pois[].accessNote`. */
export function accessNote(tags: Readonly<Record<string, string>>): string | null {
  return tags['access'] === 'customers' ? 'customers' : null;
}

/**
 * Nom d'un POI automatique : `name`, à défaut `brand`, à défaut `operator`, à
 * défaut aucun nom. Les variantes linguistiques `name:*` ne sont pas utilisées.
 */
export function poiName(tags: Readonly<Record<string, string>>): string | null {
  for (const key of ['name', 'brand', 'operator']) {
    const value = tags[key];
    if (value !== undefined && value.trim().length > 0) return value;
  }
  return null;
}

function firstTag(tags: Readonly<Record<string, string>>, ...keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = tags[key];
    if (value !== undefined && value.trim().length > 0) return value;
  }
  return null;
}

/** Objet reçu, interprété selon les correspondances et les filtres. */
export interface InterpretedObject {
  readonly object: OsmObject;
  readonly categories: readonly CategoryKey[];
  readonly matchedTags: Readonly<Record<string, string>>;
  readonly name: string | null;
  readonly openingHours: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly website: string | null;
  readonly accessNote: string | null;
  readonly exclusion: ExclusionVerdict;
}

/**
 * Interprète un objet reçu. Retourne `null` lorsque aucune catégorie ne
 * correspond, ou lorsque aucune de ses catégories n'est activée : un objet dont
 * aucune catégorie n'est activée n'est pas retenu.
 */
export function interpretObject(
  object: OsmObject,
  enabledCategories: ReadonlySet<CategoryKey>,
): InterpretedObject | null {
  const categories = categoriesForTags(object.tags);
  if (categories.length === 0) return null;
  // Retenu dès qu'au moins une de ses catégories est activée, et conserve alors
  // la totalité de ses catégories, y compris celles qui sont désactivées.
  if (!categories.some((key) => enabledCategories.has(key))) return null;

  return {
    object,
    categories,
    matchedTags: matchedTags(object.tags, categories),
    name: poiName(object.tags),
    openingHours: firstTag(object.tags, 'opening_hours'),
    phone: firstTag(object.tags, 'phone', 'contact:phone'),
    email: firstTag(object.tags, 'email', 'contact:email'),
    website: firstTag(object.tags, 'website', 'contact:website'),
    accessNote: accessNote(object.tags),
    exclusion: evaluateExclusion(object.tags, categories),
  };
}
